import type {
  CreateCustomerOrderRequest,
  CreateKotRequest,
  CreateOrderRequest,
  Order,
  OrderFilters,
  OrderForCustomer,
  PayOrderRequest,
  UpdateKotItemRequest,
  UpdateKotRequest,
  UpdateOrderRequest,
} from '@billx/types';

import { customerApiClient, userApiClient } from '../clients';
import type { NucleusServiceResponse } from './types';
import { handleAxiosError } from './utils';
export class OrderService {
  /**
   * Create a new order
   */
  static async createOrder(
    orderData: CreateOrderRequest
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.post('/orders', orderData);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create order');
    }
  }

  static async createOrderForCustomer(
    orderData: CreateCustomerOrderRequest,
    businessId: string
  ): Promise<NucleusServiceResponse<OrderForCustomer>> {
    try {
      const orderResult = await customerApiClient.post(
        '/customer/orders',
        orderData,
        { params: { businessId } }
      );

      return orderResult.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create customer order');
    }
  }

  /**
   * Fetch orders with optional filters and pagination
   */
  static async fetchOrders(
    filters: OrderFilters = {}
  ): Promise<NucleusServiceResponse<Order[]>> {
    try {
      const params = new URLSearchParams();

      if (filters.businessId) {
        params.append('businessId', filters.businessId);
      }
      if (filters.outletId) {
        params.append('outletId', filters.outletId);
      }
      if (filters.status?.length && filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters.orderType?.length && filters.orderType.length > 0) {
        params.append('orderType', filters.orderType.join(','));
      }
      if (filters.paymentMethod?.length && filters.paymentMethod.length > 0) {
        params.append('paymentMethod', filters.paymentMethod.join(','));
      }
      if (filters.customerPhone) {
        params.append('customerPhone', filters.customerPhone);
      }
      if (filters.createdBy) {
        params.append('createdBy', filters.createdBy);
      }
      if (filters.startDate) {
        params.append('dateFrom', filters.startDate.toISOString());
      }
      if (filters.endDate) {
        params.append('dateTo', filters.endDate.toISOString());
      }

      const response = await userApiClient.get('/orders', { params });

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch orders');
    }
  }

  /**
   * Fetch a single order by ID
   */
  static async fetchOrderById(
    orderId: string
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.get(`/orders/${orderId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch order');
    }
  }

  /**
   * Fetch a single order by customer ID
   */
  static async fetchOrderByCustomerId(
    customerId: string,
    businessId: string
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await customerApiClient.get(
        `/customer/orders/${customerId}`,
        { params: { businessId } }
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to fetch order');
    }
  }

  /**
   * Update an order
   */
  static async updateOrder(
    orderId: string,
    updateData: UpdateOrderRequest
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.patch(
        `/orders/${orderId}`,
        updateData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update order');
    }
  }

  /**
   * Update an order
   */
  static async payOrder(
    orderId: string,
    payData: PayOrderRequest
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.patch(
        `/orders/${orderId}/pay`,
        payData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update order');
    }
  }

  /**
   * Delete an order
   */
  static async deleteOrder(
    orderId: string
  ): Promise<NucleusServiceResponse<void>> {
    try {
      const response = await userApiClient.delete(`/orders/${orderId}`);

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete order');
    }
  }

  // === KOT Operations (return full Order) ===

  /**
   * Create a new KOT for an order
   */
  static async createKot(
    orderId: string,
    kotData: CreateKotRequest
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.post(
        `/orders/${orderId}/kots`,
        kotData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to create KOT');
    }
  }

  /**
   * Update a KOT
   */
  static async updateKot(
    orderId: string,
    kotId: string,
    updateData: UpdateKotRequest
  ): Promise<NucleusServiceResponse<Order | null>> {
    try {
      const response = await userApiClient.patch(
        `/orders/${orderId}/kots/${kotId}`,
        updateData
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update KOT');
    }
  }

  /**
   * Update a KOT item
   */
  static async updateKotItem(
    orderId: string,
    kotId: string,
    itemId: string,
    data: UpdateKotItemRequest
  ): Promise<NucleusServiceResponse<Order>> {
    try {
      const response = await userApiClient.patch(
        `/orders/${orderId}/kots/${kotId}/items/${itemId}`,
        data
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to update KOT item');
    }
  }

  /**
   * Delete a KOT
   */
  static async deleteKot(
    orderId: string,
    kotId: string
  ): Promise<NucleusServiceResponse<Order | null>> {
    try {
      const response = await userApiClient.delete(
        `/orders/${orderId}/kots/${kotId}`
      );

      return response.data;
    } catch (error) {
      return handleAxiosError(error, 'Failed to delete KOT');
    }
  }
}
